import { action, createRequestTypes } from '@/utils/actions.utils'
import { projectService } from '@/services'
import { uiActions, projectsActions } from '@/redux/actions'
import Toggles from '@domain/toggles/Toggles'
import TogglesV2 from '@domain/toggles/TogglesManager'
import { ERROR_403_PATH } from '@/constants'

export const GET_PROJECT = createRequestTypes('GET_PROJECT')
const getProject = ({
	projectId,
	lang,
	withPrices = false,
	parseProducts = false,
	hotUpdate = false,
}) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, hotUpdate }))
			let res
			if (Toggles.isEnabled('getProjectDetailsByV3')) {
				try {
					res = await projectService.getProjectV3({ projectId, lang, withPrices, isProjectPage: parseProducts })
				} catch (error) {
					if (error?.response?.status === 410) {
						const redirectTo = error.response?.data?.details?.redirectTo
						if (redirectTo) {
							const path = `/project/${redirectTo}`
							dispatch(uiActions.navigateTo({ path }))
						}
					} else {
						throw error
					}
				}
			} else {
				res = await projectService.getProjectv2({ projectId, lang, withPrices, isProjectPage: parseProducts })
			}
			return dispatch(success(res?.data))
		} catch (error) {
			if (error?.response?.status === 403) {
				dispatch(uiActions.navigateTo({ path: ERROR_403_PATH }))
			}

			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GET_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_PROJECT.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_PROJECT.FAILURE, null, err)
	}
}

export const GET_CRM_ONLY_PRODUCTS = createRequestTypes('GET_CRM_ONLY_PRODUCTS')
const getCrmOnlyProducts = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(action(GET_CRM_ONLY_PRODUCTS.REQUEST))
			const res = await projectService.getCrmOnlyProducts(projectId)
			return dispatch(action(GET_CRM_ONLY_PRODUCTS.SUCCESS, res?.data))
		} catch (error) {
			// CRM-only products are supplementary: never break the project view on failure.
			return dispatch(action(GET_CRM_ONLY_PRODUCTS.FAILURE, null, error))
		}
	}
}

export const CREATE_PROJECT = createRequestTypes('CREATE_PROJECT')
const createProject = ({ catalogId, products = [], config, name, redirectTo, v = 1 }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			let res;
			if (v == 1) {
				res = await projectService.createProject({
					catalogId,
					products,
					name
				})
				if (Toggles.isEnabled('upsertVersionD365')) {
					await projectService.upsertVersionV2(res.data.id)
					await projectService.setProjectNameV2(res.data.id, name)
				}
			} else {
				res = await projectService.createProjectv2({
					catalogId,
					products,
					name,
					config
				})
			}

			const successResponse = await dispatch(success({ id: res.data.id, name }))

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', res.data.id)
				dispatch(uiActions.navigateTo({ path }))
			}

			return successResponse
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(CREATE_PROJECT.REQUEST)
	}
	function success(payload) {
		return action(CREATE_PROJECT.SUCCESS, payload)
	}
	function failure(err) {
		return action(CREATE_PROJECT.FAILURE, null, err)
	}
}

const createProjectWithReferences = ({ catalogId, collection, referencesDetails, projectName, redirectTo }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.createProjectWithReferences({
				catalogId,
				collection,
				referencesDetails,
				projectName
			})

			const successResponse = await dispatch(
				success({
					...res.data,
					name: projectName
				})
			)

			if (Toggles.isEnabled('upsertVersionD365')) {
				await projectService.upsertVersionV2(res.data.id)
				await projectService.setProjectNameV2(res.data.id, projectName)
			}

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', res.data.id)
				dispatch(uiActions.navigateTo({ path }))
			}

			return successResponse
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(CREATE_PROJECT.REQUEST)
	}
	function success(payload) {
		return action(CREATE_PROJECT.SUCCESS, payload)
	}
	function failure(err) {
		return action(CREATE_PROJECT.FAILURE, null, err)
	}
}

export const UPDATE_PROJECT = createRequestTypes('UPDATE_PROJECT')
const updateProject = ({ projectId, fieldsToUpdate, hotUpdate = true, redirectTo }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, hotUpdate }))

			await projectService.updateProject({ projectId, fieldsToUpdate })
			if (Toggles.isEnabled('upsertVersionD365')) {
				await projectService.upsertVersionV2(projectId)
			}

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', projectId)
				dispatch(uiActions.navigateTo({ path }))
			}

			return dispatch(success(fieldsToUpdate))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(UPDATE_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(UPDATE_PROJECT.SUCCESS, payload)
	}
	function failure(err) {
		return action(UPDATE_PROJECT.FAILURE, null, err)
	}
}

export const DELETE_PROJECT = createRequestTypes('DELETE_PROJECT')
const deleteProject = ({ projectId, projectName, history }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))

			await projectService.deleteProject(projectId)
			history && history.push(`/`)
			return dispatch(success({ projectId, projectName }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(DELETE_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(DELETE_PROJECT.SUCCESS, payload)
	}
	function failure(err) {
		return action(DELETE_PROJECT.FAILURE, null, err)
	}
}
export const DUPLICATE_PROJECT = createRequestTypes('DUPLICATE_PROJECT')
const duplicateProject = ({ projectId, redirectToProject = false, resetProjectsList = false, showError = true }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.duplicateProject(projectId)

			if (redirectToProject) {
				dispatch(uiActions.navigateTo({ path: `/project/${res.data.id}` }))
			}
			if (resetProjectsList) {
				dispatch(projectsActions.fetchProjectList({ itemsToLoad: 4, resetList: true }))
			}
			return dispatch(success({ id: res.data.id }))
		} catch (error) {
			return dispatch(failure(error, showError))
		}
	}

	function request(payload) {
		return action(DUPLICATE_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(DUPLICATE_PROJECT.SUCCESS, payload)
	}
	function failure(err, showError) {
		return action(DUPLICATE_PROJECT.FAILURE, null, err, showError)
	}
}

export const VERSION_PROJECT = createRequestTypes('VERSION_PROJECT')
const versionProject = ({ projectId, redirectToProject = false, resetProjectsList = false, showError = true, email = null }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.versionProject(projectId, email)

			if (redirectToProject) {
				dispatch(uiActions.navigateTo({ path: `/project/${res.data.id}` }))
			}
			if (resetProjectsList) {
				dispatch(projectsActions.fetchProjectList({ itemsToLoad: 4, resetList: true }))
			}

			return dispatch(success({ id: res.data.id }))

		} catch (error) {
			return dispatch(failure(error, showError))
		}
	}

	function request(payload) {
		return action(VERSION_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(VERSION_PROJECT.SUCCESS, payload)
	}
	function failure(err, showError) {
		return action(VERSION_PROJECT.FAILURE, null, err, showError)
	}
}

export const GET_PROJECT_VERSIONS = createRequestTypes('GET_PROJECT_VERSIONS')
const getProjectVersions = ({
	projectId,
	hotUpdate = false,
}) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, hotUpdate }))
			const res = await projectService.getProjectVersions(projectId)
			return dispatch(success(res.data))
		} catch (error) {
			if (error?.response?.status === 403) {
				dispatch(uiActions.navigateTo({ path: ERROR_403_PATH }))
			}

			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GET_PROJECT_VERSIONS.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_PROJECT_VERSIONS.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_PROJECT_VERSIONS.FAILURE, null, err)
	}
}


export const ADD_PRODUCT = createRequestTypes('ADD_PRODUCT')
const addProduct = ({ projectId, product, redirectTo }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			await projectService.addProduct({
				projectId,
				product
			})
			if (Toggles.isEnabled('upsertVersionD365')) {
				await projectService.upsertVersionV2(projectId)
			}

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', projectId)
				dispatch(uiActions.navigateTo({ path }))
			}
			return dispatch(success({ projectId, product }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(ADD_PRODUCT.REQUEST, payload)
	}
	function success(payload) {
		return action(ADD_PRODUCT.SUCCESS, payload)
	}
	function failure(err) {
		return action(ADD_PRODUCT.FAILURE, null, err)
	}
}

export const ADD_PRODUCTS = createRequestTypes('ADD_PRODUCTS')
const addProducts = ({ projectId, products, redirectTo, config, v = 1 }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			if (v === 1) {
				await projectService.addProducts({
					projectId,
					products
				})
				if (Toggles.isEnabled('upsertVersionD365')) {
					await projectService.upsertVersionV2(projectId)
				}
			} else {
				await projectService.addProductsV2({
					projectId,
					products,
					config
				})
			}
			if (redirectTo) {
				const path = redirectTo.replace(':projectId', projectId)
				dispatch(uiActions.navigateTo({ path }))
			}
			return dispatch(success({ projectId, products }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(ADD_PRODUCTS.REQUEST, payload)
	}
	function success(payload) {
		return action(ADD_PRODUCTS.SUCCESS, payload)
	}
	function failure(err) {
		return action(ADD_PRODUCTS.FAILURE, null, err)
	}
}

export const ADD_PRODUCT_BY_REF = createRequestTypes('ADD_PRODUCT_BY_REF')
const addProductByRef = ({ projectId, reference, lang, withPrices }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, reference }))

			await projectService.addProductByRef({ projectId, reference })
			await dispatch(projectActions.getProject({ projectId, lang, withPrices, hotUpdate: true }))
			return dispatch(success({ projectId, reference }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(ADD_PRODUCT_BY_REF.REQUEST, payload)
	}
	function success(payload) {
		return action(ADD_PRODUCT_BY_REF.SUCCESS, payload)
	}
	function failure(err) {
		return action(ADD_PRODUCT_BY_REF.FAILURE, null, err)
	}
}

export const ADD_PRODUCTS_BY_REF = createRequestTypes('ADD_PRODUCTS_BY_REF')
const addProductsByRef = ({ projectId, collection, referencesDetails, redirectTo }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			await projectService.addProductsByRef({
				projectId,
				collection,
				referencesDetails
			})

			if (Toggles.isEnabled('upsertVersionD365')) {
				await projectService.upsertVersionV2(projectId)
			}

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', projectId)
				dispatch(uiActions.navigateTo({ path }))
			}
			return dispatch(success({ projectId, collection, referencesDetails }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(ADD_PRODUCTS_BY_REF.REQUEST, payload)
	}
	function success(payload) {
		return action(ADD_PRODUCTS_BY_REF.SUCCESS, payload)
	}
	function failure(err) {
		return action(ADD_PRODUCTS_BY_REF.FAILURE, null, err)
	}
}

export const CLONE_PRODUCT = createRequestTypes('CLONE_PRODUCT')
const cloneProduct = ({ projectId, productId, lang, withPrices, product, catalog }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, productId }))
			await projectService.cloneProduct({ projectId, productId, product, catalog })



			await dispatch(projectActions.getProject({ projectId, lang, withPrices, hotUpdate: true }))
			return dispatch(success({ projectId, productId }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(CLONE_PRODUCT.REQUEST, payload)
	}
	function success(payload) {
		return action(CLONE_PRODUCT.SUCCESS, payload)
	}
	function failure(err) {
		return action(CLONE_PRODUCT.FAILURE, null, err)
	}
}

export const DELETE_PRODUCT = createRequestTypes('DELETE_PRODUCT')
const deleteProduct = ({ projectId, productId }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, productId }))

			await projectService.deleteProduct({ projectId, productId })

			return dispatch(success({ projectId, productId }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(DELETE_PRODUCT.REQUEST, payload)
	}
	function success(payload) {
		return action(DELETE_PRODUCT.SUCCESS, payload)
	}
	function failure(err) {
		return action(DELETE_PRODUCT.FAILURE, null, err)
	}
}

export const UPDATE_PRODUCT = createRequestTypes('UPDATE_PRODUCT')
const updateProduct = ({
	projectId,
	productId,
	attributesToUpdate,
	redirectTo,
	config,
	products,
	v = 1,
	showError = true
}) => {
	return async (dispatch, getState) => {
		try {
			await dispatch(request())
			const userEmail = getState().auth.account?.email
			if (v == 1) {
				await projectService.updateProduct({ projectId, productId, attributesToUpdate, userEmail })
				if (!TogglesV2.isEnabledForEmail('enableSceneMode', userEmail)) {
					await projectService.upsertVersionV2(projectId)
				}
			} else {
				await projectService.updateProjectv2({ projectId, products, config })
			}

			if (redirectTo) {
				const path = redirectTo.replace(':projectId', projectId)
				dispatch(uiActions.navigateTo({ path }))
			}

			return dispatch(success({ projectId, productId: productId || products[0]._id, attributesToUpdate }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(UPDATE_PRODUCT.REQUEST)
	}
	function success(payload) {
		return action(UPDATE_PRODUCT.SUCCESS, payload)
	}
	function failure(err) {
		return action(UPDATE_PRODUCT.FAILURE, null, err, showError)
	}
}

export const SET_PRODUCT_NAME = createRequestTypes('SET_PRODUCT_NAME')
const setProductName = ({ projectId, productId, name }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			await projectService.setProductName({ projectId, productId, name })
			return dispatch(success({ projectId, productId, name }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}
	function request() { return action(SET_PRODUCT_NAME.REQUEST) }
	function success(payload) { return action(SET_PRODUCT_NAME.SUCCESS, payload) }
	function failure(err) { return action(SET_PRODUCT_NAME.FAILURE, null, err, false) }
}

export const SET_COMPOSITION_NAME = createRequestTypes('SET_COMPOSITION_NAME')
const setCompositionName = ({ projectId, productId, compositionId, name }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			await projectService.setCompositionName({ projectId, productId, compositionId, name })
			return dispatch(success({ projectId, productId, compositionId, name }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}
	function request() { return action(SET_COMPOSITION_NAME.REQUEST) }
	function success(payload) { return action(SET_COMPOSITION_NAME.SUCCESS, payload) }
	function failure(err) { return action(SET_COMPOSITION_NAME.FAILURE, null, err, false) }
}

export const SET_ARTICLE_NAME = createRequestTypes('SET_ARTICLE_NAME')
const setArticleName = ({ projectId, productId, compositionId, articleId, name }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			await projectService.setProductName({ projectId, productId: articleId, name })
			return dispatch(success({ projectId, productId, compositionId, articleId, name }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}
	function request() { return action(SET_ARTICLE_NAME.REQUEST) }
	function success(payload) { return action(SET_ARTICLE_NAME.SUCCESS, payload) }
	function failure(err) { return action(SET_ARTICLE_NAME.FAILURE, null, err, false) }
}

export const GET_PROJECT_TOOLTIP_INFO = createRequestTypes('GET_PROJECT_TOOLTIP_INFO')
const getTooltipInfo = ({ projectId }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.getTooltipInfo({ projectId })
			return dispatch(
				success({
					id: projectId,
					...res.data
				})
			)
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(GET_PROJECT_TOOLTIP_INFO.REQUEST)
	}
	function success(payload) {
		return action(GET_PROJECT_TOOLTIP_INFO.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_PROJECT_TOOLTIP_INFO.FAILURE, null, err, false)
	}
}

export const CREATE_REQUEST_BUDGET = createRequestTypes('CREATE_REQUEST_BUDGET')
const createRequestBudget = ({ lang, files, siteLocation, catalogId, description, project }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.createRequestBudget({
				lang,
				files,
				siteLocation,
				catalogId,
				description,
				project
			})
			dispatch(success(res.data))
			return dispatch(uiActions.setActionDone(action(CREATE_REQUEST_BUDGET.SUCCESS, res.data)))
		} catch (error) {
			dispatch(failure(error))
			return dispatch(uiActions.setActionDone(action(CREATE_REQUEST_BUDGET.FAILURE, null, error)))
		}
	}

	function request() {
		return action(CREATE_REQUEST_BUDGET.REQUEST)
	}
	function success(payload) {
		return action(CREATE_REQUEST_BUDGET.SUCCESS, payload)
	}
	function failure(err) {
		return action(CREATE_REQUEST_BUDGET.FAILURE, null, err)
	}
}

export const UPDATE_PROJECT_THIRD_PARTY = 'UPDATE_PROJECT_THIRD_PARTY'
const updateProjectThirdParty = (payload) => {
	return action(UPDATE_PROJECT_THIRD_PARTY, payload)
}
export const EMPTY_PROJECT_DETAILS = 'EMPTY_PROJECT_DETAILS'
const emptyProjectDetails = () => {
	return action(EMPTY_PROJECT_DETAILS)
}

export const FETCH_PROJECT_TECH_INFO = createRequestTypes('FETCH_PROJECT_TECH_INFO')
const fetchProjectTechInfo = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.getProjectTechInfo(projectId)
			return dispatch(success(res.data))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(FETCH_PROJECT_TECH_INFO.REQUEST)
	}
	function success(payload) {
		return action(FETCH_PROJECT_TECH_INFO.SUCCESS, payload)
	}
	function failure(err) {
		return action(FETCH_PROJECT_TECH_INFO.FAILURE, null, err)
	}
}

export const FETCH_PROJECT_CONTEXT = createRequestTypes('FETCH_PROJECT_CONTEXT')
const fetchProjectContext = ({ projectId, lang }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			const res = await projectService.getProject({ projectId, lang })
			return dispatch(success(res.data))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(FETCH_PROJECT_CONTEXT.REQUEST)
	}
	function success(payload) {
		return action(FETCH_PROJECT_CONTEXT.SUCCESS, payload)
	}
	function failure(err) {
		return action(FETCH_PROJECT_CONTEXT.FAILURE, null, err)
	}
}

export const FETCH_PROJECT_PERSONALIZED_PRODUCT = createRequestTypes('FETCH_PROJECT_PERSONALIZED_PRODUCT')
const getProjectPersonalizedProducts = ({ projectId, lang }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			// DV-6260: the sr-projects products endpoint does not mark isDiscontinued, so
			// discontinued personalized variants leaked into the budget request. Route through
			// /v2/project-to-budget, which enriches isDiscontinued (RequestBudget.context then
			// filters them out).
			const res = await projectService.getProjectToBudgetV2({ projectId, lang })
			return dispatch(success(res.data))
		} catch (error) {
			if (error?.response?.status === 403) {
				dispatch(uiActions.navigateTo({ path: ERROR_403_PATH }))
			}
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(FETCH_PROJECT_PERSONALIZED_PRODUCT.REQUEST)
	}
	function success(payload) {
		return action(FETCH_PROJECT_PERSONALIZED_PRODUCT.SUCCESS, payload)
	}
	function failure(err) {
		return action(FETCH_PROJECT_PERSONALIZED_PRODUCT.FAILURE, null, err)
	}
}

export const MODAL_PROJECT_CLOSE = 'MODAL_PROJECT_CLOSE'
const modalProjectClose = (payload) => {
	return action(MODAL_PROJECT_CLOSE, payload)
}

export const NEW_THIRD_PARTY_PROJECT = 'NEW_THIRD_PARTY_PROJECT'
const newThirdPartyProject = (payload) => {
	return action(NEW_THIRD_PARTY_PROJECT, payload)
}

export const ADD_MODEL_STANDARD = 'ADD_MODEL_STANDARD'
const addModelStandard = (payload) => {
	return action(ADD_MODEL_STANDARD, payload)
}

export const CLOSE_MODAL_ADD_PRODUCT = 'CLOSE_MODAL_ADD_PRODUCT'
const closeModalAddProduct = (payload) => {
	return action(CLOSE_MODAL_ADD_PRODUCT, payload)
}
export const UPDATE_PROJECT_NAME = createRequestTypes('UPDATE_PROJECT_NAME')
const updateProjectName = ({ projectId, newName }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.updateProjectName(projectId, newName)
			return dispatch(success(res.data))

		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action('request', payload)
	}
	function success(payload) {
		return action('success', payload)
	}
	function failure(err) {
		return action('failure', null, err)
	}
}

const getProjectName = ({ projectId }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.getProjectName(projectId)
			return dispatch(success(res.data))

		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action('request', payload)
	}
	function success(payload) {
		return action('success', payload)
	}
	function failure(err) {
		return action('failure', null, err)
	}

}

const getOrdersResume = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.getOrdersResume(projectId)
			return dispatch(success(res.data))
		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action('request', payload)
	}
	function success(payload) {
		return action('success', payload)
	}
	function failure(err) {
		return action('failure', null, err)
	}
}
export const GET_ORDERS_RESUME = createRequestTypes('GET_ORDERS_RESUME')

const getAllOrdersByProjectId = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.getAllOrdersByProjectId(projectId)
			return dispatch(success(res.data))
		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action('request', payload)
	}
	function success(payload) {
		return action('success', payload)
	}
	function failure(err) {
		return action('failure', null, err)
	}
}

export const FORK_PROJECT = createRequestTypes('FORK_PROJECT')
const forkProject = ({ projectId, redirectToProject = true }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.forkProject(projectId)
			if (redirectToProject) {
				dispatch(uiActions.navigateTo({ path: `/project/${res.data.result}` }))
			}
			return dispatch(success({ id: res.data.result }))
		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(FORK_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(FORK_PROJECT.SUCCESS, payload)
	}
	function failure(err, showError) {
		return action(FORK_PROJECT.FAILURE, null, err, showError)
	}
}

export const DISCARD_PROJECT = createRequestTypes('DISCARD_PROJECT')
const discardProject = ({ projectId, redirectToParent = true }) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			const res = await projectService.discardProject(projectId)
			if (redirectToParent && res.data.result) {
				dispatch(uiActions.navigateTo({ path: `/project/${res.data.result}` }))
			} else {
				dispatch(uiActions.navigateTo({ path: `/` }))
			}
			return dispatch(success(res.data))
		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(DISCARD_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(DISCARD_PROJECT.SUCCESS, payload)
	}
	function failure(err, showError) {
		return action(DISCARD_PROJECT.FAILURE, null, err, showError)
	}
}

export const SHARE_PROJECT = createRequestTypes('SHARE_PROJECT')
const shareProjectWithEmail = (projectId, email) => {
	return async (dispatch) => {
		try {
			await dispatch(request())
			await projectService.shareProjectWithEmail(projectId, email)
			return dispatch(success(email))
		} catch (error) {
			dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(SHARE_PROJECT.REQUEST, payload)
	}
	function success(payload) {
		return action(SHARE_PROJECT.SUCCESS, payload)
	}
	function failure(err, showError) {
		return action(SHARE_PROJECT.FAILURE, null, err, showError)
	}
}

export const GET_OPPORTUNITY_INSTALLATION_DATE = createRequestTypes('GET_OPPORTUNITY_INSTALLATION_DATE')
const getOpportunityInstallationDate = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))
			const res = await projectService.getOpportunityInstallationDate(projectId)
			return dispatch(success({ projectId, installationDate: res.data.scheduledInstallationDate }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GET_OPPORTUNITY_INSTALLATION_DATE.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_OPPORTUNITY_INSTALLATION_DATE.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_OPPORTUNITY_INSTALLATION_DATE.FAILURE, null, err)
	}
}

export const UPDATE_OPPORTUNITY_INSTALLATION_DATE = createRequestTypes('UPDATE_OPPORTUNITY_INSTALLATION_DATE')
const updateOpportunityInstallationDate = ({ projectId, installationDate }) => {

	// Convert local date to UTC date to avoid timezone issues
	const dateToSend = installationDate instanceof Date
		? new Date(Date.UTC(
			installationDate.getFullYear(),
			installationDate.getMonth(),
			installationDate.getDate(),
			12, 0, 0
		))
		: installationDate;

	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, installationDate: dateToSend }))
			await projectService.updateOpportunityInstallationDate({ projectId, installationDate: dateToSend })
			return dispatch(success({ projectId, installationDate: dateToSend }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(UPDATE_OPPORTUNITY_INSTALLATION_DATE.REQUEST, payload)
	}
	function success(payload) {
		return action(UPDATE_OPPORTUNITY_INSTALLATION_DATE.SUCCESS, payload)
	}
	function failure(err) {
		return action(UPDATE_OPPORTUNITY_INSTALLATION_DATE.FAILURE, null, err)
	}
}

export const GET_PROJECT_SCENES = createRequestTypes('GET_PROJECT_SCENES')
const getProjectScenes = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))
			const res = await projectService.getProjectScenes(projectId)
			return dispatch(success({ projectId, scenes: res.data }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GET_PROJECT_SCENES.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_PROJECT_SCENES.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_PROJECT_SCENES.FAILURE, null, err)
	}
}

export const CREATE_PROJECT_SCENE = createRequestTypes('CREATE_PROJECT_SCENE')
const createProjectScene = (projectId, { name, description, order }) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId, name, description, order }))
			const res = await projectService.createProjectScene(projectId, { name, description, order })
			dispatch(success({ projectId, scene: res.data }))
			return res.data
		} catch (error) {
			dispatch(failure(error))
			return null
		}
	}

	function request(payload) {
		return action(CREATE_PROJECT_SCENE.REQUEST, payload)
	}
	function success(payload) {
		return action(CREATE_PROJECT_SCENE.SUCCESS, payload)
	}
	function failure(err) {
		return action(CREATE_PROJECT_SCENE.FAILURE, null, err)
	}
}

export const DELETE_PROJECT_SCENE = createRequestTypes('DELETE_PROJECT_SCENE')
const deleteProjectScene = (sceneId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ sceneId }))
			await projectService.deleteProjectScene(sceneId)
			return dispatch(success({ sceneId }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(DELETE_PROJECT_SCENE.REQUEST, payload)
	}
	function success(payload) {
		return action(DELETE_PROJECT_SCENE.SUCCESS, payload)
	}
	function failure(err) {
		return action(DELETE_PROJECT_SCENE.FAILURE, null, err)
	}
}

export const COPY_PROJECT_SCENE = createRequestTypes('COPY_PROJECT_SCENE')
const copyProjectScene = (sceneId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ sceneId }))
			const res = await projectService.copyProjectScene(sceneId)
			return dispatch(success({ scene: res.data }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(COPY_PROJECT_SCENE.REQUEST, payload)
	}
	function success(payload) {
		return action(COPY_PROJECT_SCENE.SUCCESS, payload)
	}
	function failure(err) {
		return action(COPY_PROJECT_SCENE.FAILURE, null, err)
	}
}

export const UPDATE_PROJECT_SCENE = createRequestTypes('UPDATE_PROJECT_SCENE')
const updateProjectScene = (sceneId, payload) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ sceneId, ...payload }))
			const res = await projectService.updateProjectScene(sceneId, payload)
			return dispatch(success({ scene: res.data }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(UPDATE_PROJECT_SCENE.REQUEST, payload)
	}
	function success(payload) {
		return action(UPDATE_PROJECT_SCENE.SUCCESS, payload)
	}
	function failure(err) {
		return action(UPDATE_PROJECT_SCENE.FAILURE, null, err)
	}
}

export const GET_PROJECT_ITEM_LIGHTS = createRequestTypes('GET_PROJECT_ITEM_LIGHTS')
const getProjectItemLights = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))
			const res = await projectService.getProjectItemLights(projectId)
			return dispatch(success({ projectId, itemLights: res.data.itemLights || res.data }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GET_PROJECT_ITEM_LIGHTS.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_PROJECT_ITEM_LIGHTS.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_PROJECT_ITEM_LIGHTS.FAILURE, null, err)
	}
}

export const DOWNLOAD_PROJECT_SCENES = createRequestTypes('DOWNLOAD_PROJECT_SCENES')
const downloadProjectScenes = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))
			const res = await projectService.downloadProjectScenes(projectId)
			return dispatch(success({ projectId, data: res.data, headers: res.headers }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(DOWNLOAD_PROJECT_SCENES.REQUEST, payload)
	}
	function success(payload) {
		return action(DOWNLOAD_PROJECT_SCENES.SUCCESS, payload)
	}
	function failure(err) {
		return action(DOWNLOAD_PROJECT_SCENES.FAILURE, null, err)
	}
}

export const GENERATE_PROJECT_ITEM_LIGHTS = createRequestTypes('GENERATE_PROJECT_ITEM_LIGHTS')
const generateProjectItemLights = (projectId) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ projectId }))
			const res = await projectService.generateProjectItemLights(projectId)
			return dispatch(success({ projectId, itemLights: res.data.itemLights || res.data }))
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request(payload) {
		return action(GENERATE_PROJECT_ITEM_LIGHTS.REQUEST, payload)
	}
	function success(payload) {
		return action(GENERATE_PROJECT_ITEM_LIGHTS.SUCCESS, payload)
	}
	function failure(err) {
		return action(GENERATE_PROJECT_ITEM_LIGHTS.FAILURE, null, err)
	}
}

const projectActions = {
	getProject,
	getCrmOnlyProducts,
	createProject,
	createProjectWithReferences,
	deleteProject,
	duplicateProject,
	versionProject,
	getProjectVersions,
	emptyProjectDetails,
	updateProject,
	addProduct,
	addProducts,
	addProductByRef,
	addProductsByRef,
	cloneProduct,
	deleteProduct,
	setProductName,
	setCompositionName,
	setArticleName,
	updateProduct,
	getTooltipInfo,
	createRequestBudget,
	updateProjectThirdParty,
	fetchProjectTechInfo,
	fetchProjectContext,
	getProjectPersonalizedProducts,
	modalProjectClose,
	addModelStandard,
	closeModalAddProduct,
	newThirdPartyProject,
	updateProjectName,
	getProjectName,
	getOrdersResume,
	getAllOrdersByProjectId,
	forkProject,
	discardProject,
	shareProjectWithEmail,
	updateOpportunityInstallationDate,
	getOpportunityInstallationDate,
	getProjectScenes,
	createProjectScene,
	deleteProjectScene,
	copyProjectScene,
	updateProjectScene,
	getProjectItemLights,
	generateProjectItemLights,
	downloadProjectScenes
}

export default projectActions